import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Typography,
} from '@mui/material';
import { AnyListItemGeoEvent } from 'features/geoEvents/anyGeoEvent.types';
import { useLocationV2Enabled } from 'features/locations/hooks/useLocationV2Enabled';
import { useTranslation } from 'react-i18next';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Link } from 'react-router-dom';
import { GeoEventType } from 'features/geoEvents/geoEvent.types';
import { DetailedHTMLProps, ImgHTMLAttributes } from 'react';
import {
  isFloodingGeoEvent,
  isHazardGeoEvent,
  isLocationGeoEvent,
  isWildfireGeoEvent,
} from 'features/geoEvents/geoEvent.utils';
import {
  getFloodingEventIcon,
  getHazardEventIcon,
  getLocationIcon,
  getWildfireGeoEventIcon,
} from 'features/mapping/icons';
import { LocationListItem } from 'features/locations/location.types';
import { TFunction } from 'i18next';

type AlertGeoEventsProps = {
  geoEvents: AnyListItemGeoEvent[];
};

const ROUTE_PREFIX: Record<GeoEventType, string> = {
  wildfire: '/i',
  flooding: '/i',
  hazard: '/i',
  location: '/location',
};

export const getIconRoute = (geoEvent: AnyListItemGeoEvent): string =>
  `${ROUTE_PREFIX[geoEvent.geoEventType]}/${geoEvent.id}`;

const getIconData = (
  geoEvent: AnyListItemGeoEvent,
  showLocationV2: boolean,
): string => {
  if (isWildfireGeoEvent(geoEvent)) {
    return getWildfireGeoEventIcon(geoEvent).data;
  }
  if (isFloodingGeoEvent(geoEvent)) {
    return getFloodingEventIcon(geoEvent).data;
  }
  if (isHazardGeoEvent(geoEvent)) {
    return getHazardEventIcon(geoEvent).data;
  }
  if (isLocationGeoEvent(geoEvent)) {
    return getLocationIcon(
      (geoEvent as LocationListItem).data.locationType,
      showLocationV2,
    ).data;
  }
  return '';
};

const getIconAlt = (geoEvent: AnyListItemGeoEvent, t: TFunction): string => {
  if (isWildfireGeoEvent(geoEvent)) {
    return t('alert.wildfire');
  }
  if (isFloodingGeoEvent(geoEvent)) {
    return t('alert.flood');
  }
  if (isHazardGeoEvent(geoEvent)) {
    return t('alert.hazard');
  }
  if (isLocationGeoEvent(geoEvent)) {
    return t('alert.location');
  }
  return '';
};

export const getIconProps = (
  t: TFunction,
  geoEvent: AnyListItemGeoEvent,
  showLocationV2: boolean,
): DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> => {
  const src = getIconData(geoEvent, showLocationV2);
  const alt = getIconAlt(geoEvent, t);

  if (isWildfireGeoEvent(geoEvent) || isFloodingGeoEvent(geoEvent)) {
    return { src, alt, width: 36, height: 36 };
  }

  return { src, alt, width: 36, height: 36, style: { padding: 4 } };
};

export const AlertGeoEvents = (props: AlertGeoEventsProps): JSX.Element => {
  const { geoEvents } = props;
  const { t } = useTranslation();
  const showLocationV2 = useLocationV2Enabled();

  return (
    <List
      subheader={
        <ListSubheader variant="watchduty-section-header">
          <Typography variant="h3">{t('alert.geoEvents')}</Typography>
        </ListSubheader>
      }
      disablePadding
    >
      {geoEvents.map((geoEvent) => (
        <ListItemButton
          key={geoEvent.id}
          divider
          component={Link}
          to={{
            pathname: getIconRoute(geoEvent),
            state: {
              fromRoute: window.location.pathname + window.location.search,
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 44 }}>
            {/* eslint-disable-next-line jsx-a11y/alt-text */}
            <img {...getIconProps(t, geoEvent, showLocationV2)} />
          </ListItemIcon>
          <ListItemText
            primary={geoEvent.name}
            secondary={geoEvent.address}
            slotProps={{ primary: { fontWeight: 'fontWeightBold' } }}
          />
          <ListItemIcon
            sx={(theme) => ({
              minWidth: 'fit-content',
              color: theme.palette.text.primary,
            })}
          >
            <KeyboardArrowRightIcon />
          </ListItemIcon>
        </ListItemButton>
      ))}
    </List>
  );
};
